import { sections as Api, type SectionMode, type SectionRow } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';

const MODE_LABEL: Record<SectionMode, string> = {
  auto: 'Auto — hide until it has content',
  on: 'Always show',
  off: 'Hide from everyone and from this console',
};

/**
 * Which News & Analysis sections members and visitors see.
 *
 * Auto is the default: a section stays out of the menu, the apps and the
 * website until the desk publishes the first thing in it. The editors here in
 * the console are never hidden, or nobody could write the first note.
 */
export function NewsSections({ edits, onEdit }: {
  /** Choices made but not saved yet, keyed by section. */
  edits: Record<string, SectionMode>;
  onEdit: (next: Record<string, SectionMode>) => void;
}) {
  const { data, error } = useAsync((signal) => Api.load(signal));

  /* A choice that goes back to what is saved is no change at all. */
  function pick(row: SectionRow, mode: SectionMode) {
    const next = { ...edits, [row.key]: mode };
    if (mode === row.mode) delete next[row.key];
    onEdit(next);
  }

  if (error) return <div className="err">{String((error as Error)?.message ?? error)}</div>;
  if (!data) return null;

  return (
    <div className="crd" style={{ padding: '16px 22px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>News &amp; Analysis sections</div>
      </div>
      <div style={{ fontSize: 13, color: 'var(--tx2)', marginTop: 2, marginBottom: 12, maxWidth: 720, lineHeight: 1.5 }}>
        Choose what members see in the member area, the mobile apps and on the website. With Auto, a section
        stays hidden until you publish the first item in it, then appears by itself. Auto keeps your editors in this console so you can write the first item; only "Hide from everyone" removes the editor from the sidebar too.
        Choose, then press Save at the top of the page.
      </div>
      <div className="tblx">
        <table className="tbl">
          <thead><tr><th>Section</th><th>Content</th><th>Showing now</th><th>Show</th></tr></thead>
          <tbody>
            {data.sections.map((s) => (
              <tr key={s.key}>
                <td style={{ fontWeight: 700 }}>{s.label}</td>
                <td className="muted">{s.count ? `${s.count} ${s.what}` : `No ${s.what} yet`}</td>
                <td>
                  <span className="tg" style={{ background: s.showing ? 'rgba(62,207,142,.14)' : undefined }}>
                    {s.showing ? 'visible' : 'hidden'}
                  </span>
                </td>
                <td>
                  <select className="inp" style={{ width: 'auto' }} value={edits[s.key] ?? s.mode}
                    onChange={(e) => pick(s, e.target.value as SectionMode)}>
                    {(Object.keys(MODE_LABEL) as SectionMode[]).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
